import React, { Component, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';
import { withRouter, Redirect, matchPath, generatePath } from 'react-router';
import cn from 'classnames';
import instance from 'Shared/api/instance';
import { connect } from 'react-redux';
import sEmitter from 'storage-emitter';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import PageUrls from 'Shared/constants/PageUrls.constants';

import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import Spinner from 'Shared/components/Spinner';
import { UserRole, OrganizationRole } from 'Shared/constants/types';
import { billingStates } from 'Shared/constants/constants';
import { lazyWithRetry } from 'Shared/utils/retry';
import SideNavigation from 'Shared/components/navigation/SideNavigation/SideNavigation';
import ManagerRoute from 'Shared/components/RoutesComponents/ManagerRoute';
import MobileTopNavigation from 'Shared/components/navigation/MobileTopNavigation/MobileTopNavigation';
import SimpleBulkInviteTeammatesModal from 'Shared/components/modals/SimpleAddWorkspaceModals/SimpleBulkInviteTeammatesModal/SimpleBulkInviteTeammatesModal';
import AddTeammateByEmailModal from 'Shared/components/modals/AddTeammateByEmailModal/AddTeammateByEmailModal';
import InviteToTeamModal from 'Shared/components/modals/InviteToTeamModal/InviteToTeamModal';
import ForceNewAppVersion from 'Shared/utils/ForceNewAppVersion';
import WpdsDocs from 'Wpds/docs/build/WpdsDocs';
import WpToastContainer from 'Wpds/components/toast/WpToastContainer';
import TrialExpiredModal from 'Dashboard/components/TrialExpiredModal/TrialExpiredModal';
import TransferToPIBillingModal from 'Dashboard/components/TransferToPIBillingModal/TransferToPIBillingModal';
import CharmaLegacySunsetModalForcedExperience from 'Dashboard/components/CharmaLegacySunsetModal/CharmaLegacySunsetModalForcedExperience';

// import Workspaces from './components/Workspaces/Workspaces';
// import HomePage from './components/HomePageV2/HomePageV2';
import UnsupportedBrowserAlert from './components/UnsupportedBrowserAlert/UnsupportedBrowserAlert';
import ZoomJavascriptSDKHandler from './components/ZoomJavascriptSDKHandler/ZoomJavascriptSDKHandler';
import CommentsAreaWrapper from './components/CommentsAreaWrapper/CommentsAreaWrapper';
import GoalDetailPanelWrapper from 'Shared/components/GoalDetailPanel/GoalDetailPanelWrapper';
import CreateOrUpdateGoalPageWrapper from './components/CreateOrUpdateGoalPageV3/CreateOrUpdateGoalPageWrapper';
import ZoomLandingPage from './components/ZoomLandingPage/ZoomLandingPage';
import BannerManager from './components/BannerManager/BannerManager';
import SyncPredictiveIndexAuth from './components/SyncPredictiveIndexAuth/SyncPredictiveIndexAuth';
import UpdateUserTimeZone from './components/UpdateUserTimeZone/UpdateUserTimeZone';
import MobileAddButton from 'Shared/components/MobileAddButton/MobileAddButton';
import AgreeToPiTermsModal from './components/AgreeToPiTermsModal/AgreeToPiTermsModal';
import UserBaProfileSnapshotRouteDrawer from 'Shared/components/UserBaProfileSnapshot/UserBaProfileSnapshotRouteDrawer';

// Layouts
import RouteLayoutWrapper from 'Shared/route_layouts/RouteLayoutWrapper';
import LoggedOutWrapper from 'Shared/route_layouts/LoggedOutWrapper';

import './App.scss';
import 'Wpds/styles/_common.scss';

const HomePage = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/HomePageV3/HomePage'));

const Recommendations = lazyWithRetry(() => import(/* webpackPrefetch: true */ 'Shared/components/Recommendations'));

const RecommendationAnalytics = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/Analytics/Analytics')
);
const SingleUserAnalytics = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/Analytics/SingleUserAnalytics')
);

// This is old onboard - still has some /user/signup routes
const Onboarding = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/Onboarding/Onboarding'));

// This is new V2 oboarding
const OnboardingWrapper = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/Onboarding/OnboardingWrapper')
);

const ChromeExtensionUninstall = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/ChromeExtensionUninstall/ChromeExtensionUninstall')
);
const SettingsPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/SettingsPage/SettingsPage'));
const SearchPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/SearchPage'));
const AssistantsPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/AssistantsPage/AssistantsPage')
);
const WorkspaceAcceptMember = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/WorkspaceAcceptMember/WorkspaceAcceptMember')
);

const LandingPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/LandingPage/LandingPage'));

const GoalDetailsModal = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/GoalDetailsModal/GoalDetailsModal')
);

const SignInPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/SignInPage/SignInPage'));

const SsoSignInPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/SsoSignInPage/SsoSignInPage')
);

const PasswordResetPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/PasswordResetPage/PasswordResetPage')
);

const CoachingPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/CoachingPageV2/CoachingPage')
);

const ManagerDashboard = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/ManagerDashboard/ManagerDashboard')
);

const UserProfilePage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/UserProfilePage/UserProfilePage')
);

const GoalsPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/GoalsPage/GoalsPage'));

const AcceptInvitePage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/SignUpPage/AcceptInvitePage')
);

const OrgSettingsPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/OrgSettingsPage/OrgSettingsPage')
);

const ThreeSixtyRouteWrapper = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/ThreeSixtyRouteWrapper/ThreeSixtyRouteWrapper')
);

const PerformanceReviewsRouteWrapper = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/PerformanceReviewsRouteWrapper/PerformanceReviewsRouteWrapper')
);

const WorkspacePage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/WorkspacePage/WorkspacePage')
);

const AiToolkitPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/AiToolkitPage/AiToolkitPage')
);

const UpdateAiPage = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/UpdateAiPage/UpdateAiPage'));

const UpdateAiSettingPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/UpdateAiPage/UpdateAiSettingPage')
);
const UpdateAiReportPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/UpdateAiReportPage/UpdateAiReportPage')
);

const FeedbackTemplateBuilderPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/FeedbackTemplateBuilderPage/FeedbackTemplateBuilderPage')
);

const ReviewTemplatePreviewPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/ReviewTemplatePreviewPage/ReviewTemplatePreviewPage')
);

const PerformanceReviewSharePage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ './components/PerformanceReviewSharePage/PerformanceReviewSharePage')
);

type Props = {
  app: {
    id: string;
    performMarketingPageUrl: string;
  };
  user: {
    isLoggedIn: boolean;
    id: number;
    slug: string;
    email: string;
    selectedMembership: {
      features: {
        shown: boolean;
      };
    };
    role: string;
    token: string;
    organizationRole: string;
    pioneerForConsumerDomainOrganization: boolean;
  };
  org: {
    billingState: string;
    billingVersion: number;
  };
  history: any;
  workspaceID?: string;
  location: {
    pathname: string;
    search: string;
    state: {
      modal: boolean;
      preventPreviousRouteChange: boolean;
    };
  };
  onChooseMultipleItems: Function;
  dispatch: Function;
  flags: {
    goals: boolean;
    forceNewAppVersion: boolean;
  };
};

type State = {
  redirectTo: string;
  isNavbarExpanded: boolean;
  firstLoad: boolean;
  isWpSideNavigationExpanded: boolean;
};

class App extends Component<Props, State> {
  state: State = {
    isNavbarExpanded: false,
    redirectTo: null,
    firstLoad: true,
    isWpSideNavigationExpanded: null,
  };

  previousLocation = null;

  constructor(props) {
    super(props);
    this.previousLocation = this.props.location;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    instance.interceptors.response.use(
      response => response,
      error => {
        // Setup the app to redirect to "/" upon 401
        if (error.response && error.response.status === 401) {
          that.navigateToSignInPage();
        }
        return Promise.reject(error);
      }
    );
  }

  // this allows for soft redirects of old routes to new routes
  routesToRedirect = [
    {
      from: PageUrls.WORKSPACES,
      to: PageUrls.HOME_PAGE,
    },
    {
      from: PageUrls.TEAM_PAGE_SURFACE_AI,
      to: PageUrls.SURFACEAI_PAGE,
    },
    {
      from: PageUrls.INTEGRATIONS,
      to: PageUrls.SETTINGS_INTEGRATIONS_PAGE,
    },
    {
      from: PageUrls.SETTINGS_USER_MANAGEMENT_PAGE,
      to: PageUrls.ADMIN_MEMBERS,
    },
    {
      from: PageUrls.SETTINGS_ENTERPRISE_PAGE,
      to: PageUrls.ADMIN_INTEGRATIONS,
    },
    {
      from: PageUrls.SETTINGS_BILLING_PAGE,
      to: PageUrls.HOME_PAGE,
    },
    {
      from: PageUrls.ADMIN_BILLING,
      to: PageUrls.HOME_PAGE,
    },
    {
      from: PageUrls.ANALYTICS_ADMIN,
      to: PageUrls.ADMIN_ANALYTICS,
    },
    {
      from: PageUrls.FEEDBACK_NEW,
      to: PageUrls.COACHING_FEEDBACK,
    },
    {
      from: PageUrls.DASHBOARD_PAGE,
      to: PageUrls.HOME_PAGE,
    },
    {
      from: PageUrls.WORKSPACE_HISTORY_MEETING_SUMMARIES,
      to: PageUrls.WORKSPACE_AGENDA_HISTORY,
    },
  ];

  componentDidMount() {
    const { location, user, history, app, org } = this.props;

    const isOrganizationAdmin = user.organizationRole === OrganizationRole.admin;
    const isTrialExpired = org.billingState === billingStates.TRIAL_EXPIRED;
    const isBillingVersion2 = org.billingVersion === 2;
    const isZoom = app.id === 'zoom';
    const pioneerForConsumerDomainOrganization = user.pioneerForConsumerDomainOrganization;

    if (location.search.includes('close_oauth2_screen=true')) {
      sEmitter.emit('close_oauth2_screen');

      // Not perfect, but window.opener will only be present if the current window
      // was opened by popping open oauth in a new window.  In a normal window this
      // will be null;
      if (window.opener) {
        window.close();
      }
    }

    if (location.search.includes('track_finished_onboarding=true') && !pioneerForConsumerDomainOrganization) {
      // Fire the completed onboarding (frontend) event based on the url containing a query param
      // This is needed for FB Pixel tracking to work properly
      (window as any).analytics.track('Completed Onboarding (frontend)', { value: 0.0, currency: 'USD' });
    }

    // Only reload the chrome extension on this event since other oauth will open a new window
    if (app.id === 'chrome') {
      sEmitter.on('close_oauth2_screen', () => {
        window.parent.postMessage({ type: 'RELOAD_EXTENSION_CONTENT_IFRAME' }, '*');
        window.location.reload();
      });
    }

    if (user.isLoggedIn) {
      // Set hasLoggedIn key in local storage so if they ever log out we'll know they're
      // a returning user.
      try {
        if (localStorage) localStorage.setItem('hasLoggedIn', 'true');
      } catch {} // eslint-disable-line no-empty

      const lastVisitedPage = localStorage && localStorage.getItem('lastVisitedPage');
      switch (location.pathname) {
        case '/chrome':
          if (lastVisitedPage) this.setState({ redirectTo: lastVisitedPage });
          else this.navigateToLandingPage();
          break;
        case '/dashboard':
          this.navigateToLandingPage();
          break;
        default:
          this.redirectIfNecessary();
      }
    } else if (localStorage && localStorage.getItem('hasLoggedIn') && location.pathname === PageUrls.HOME_PAGE) {
      // If they're a returning user, not logged in and loaded the root URL
      // then send them straight to login screen
      history.push('/user/login');
    }

    // If the trial is expired, and org is on the new billing state machine,
    // we want to  force admins to choose - so redirect to billing
    // do not redirect to pricing page if inside the zoom app
    if (isOrganizationAdmin && isTrialExpired && isBillingVersion2 && app.id !== 'zoom') {
      history.push(PageUrls.PLANS_PAGE);
    }

    // If App is inside of zoom, then listen to all clicks on the document and
    // check if it is a click on an anchor tag to an external URL. If it is then
    // use the openURL api to proxy through our redirect endpoint and open the
    // external link in a new window.
    if (isZoom) {
      document.addEventListener('click', e => {
        // Anchor tags inside of draft JS can have multiple span tags inside of them
        // so we need to follow the parent up 2 levels and see if one of the parents
        // is the actual anchor tag being clicked.
        let target = e.target as any;
        for (let i = 0; i < 2; i++) {
          if (target.nodeName.toLowerCase() === 'a') {
            break;
          } else {
            target = target.parentNode;
          }
        }

        if (target.nodeName.toLowerCase() === 'a') {
          e.preventDefault();
          try {
            const href = target.getAttribute('href').trim();
            const newUrl = new URL(href);
            if (newUrl.hostname !== window.location.hostname) {
              console.log('opening external zoom link');
              (window as any).zoomSdk.openUrl({
                url: `${window.location.origin}/redirects?url=${encodeURI(href)}`,
              });
            }
          } catch {
            // Catch error from trying to generate invalid URL above and do nothing
          }
        }
      });
    }

    // Rip the pre-load spinner out if it exists still
    const spinner = document.getElementsByClassName('pre-app-load-spinner');
    spinner[0]?.remove();
  }

  componentWillUpdate() {
    const { location } = this.props;
    if (
      !location.state?.preventPreviousRouteChange &&
      !location.state?.modal &&
      this.previousLocation.pathname !== location.pathname
    ) {
      this.previousLocation = location;
    }
  }

  componentDidUpdate(prevProps) {
    const { firstLoad } = this.state;
    const { location, app } = this.props;
    const { location: prevLocation } = prevProps;

    if (firstLoad) {
      // eslint-disable-next-line
      this.setState({ firstLoad: false });
    }

    this.redirectIfNecessary();

    if (app.id === 'chrome' && location.pathname !== prevLocation.pathname)
      localStorage.setItem('lastVisitedPage', location.pathname);
  }

  redirectIfNecessary = () => {
    const { location, app } = this.props;

    // If it's /workspaces, we'll allow the redirect since
    // it should be open for all people.
    if (location.pathname.includes('/recommendations')) {
      this.redirectIfHiddenOrLocked('recommendations');
    } else if (location.pathname.includes('/recommendation_analytics')) {
      this.redirectIfHiddenOrLocked('recommendation_analytics');
    } else if (location.pathname.includes('assistants/manager')) {
      this.redirectIfNotManager();
    }

    // redirect the /buy and /plans into the new perform marketing page from within the SPA
    if (location.pathname === PageUrls.PLANS_BUY_PAGE || location.pathname === PageUrls.PLANS_PAGE) {
      window.location.href = `${app.performMarketingPageUrl}?source=software-perform`;
    }
  };

  redirectIfNotManager = () => {
    const { user, history } = this.props;
    if (user.role !== UserRole.manager) {
      history.push(PageUrls.HOME_PAGE);
    }
  };

  redirectIfHiddenOrLocked = feature => {
    const { history } = this.props;

    if (!this.showFeature(feature) || this.lockFeature(feature)) {
      history.push('/dashboard');
    }
  };

  // This is responsible for routing to the proper landing page, depending
  // on what features are shown/locked.
  navigateToLandingPage = () => {
    if (this.showFeature('recommendations') && !this.lockFeature('recommendations')) {
      this.setState({ redirectTo: '/recommendations' });
    } else if (this.showFeature('recommendation_analytics') && !this.lockFeature('recommendation_analytics')) {
      this.setState({ redirectTo: '/recommendation_analytics' });
    }
  };

  navigateToSignInPage = () => {
    this.props.history.replace('/user/login');
    window.location.reload();
  };

  showFeature = feature => {
    const {
      user: { selectedMembership },
    } = this.props;
    return selectedMembership && selectedMembership.features[feature] && selectedMembership.features[feature].shown;
  };

  lockFeature = feature => {
    const {
      user: { selectedMembership },
    } = this.props;
    return selectedMembership && selectedMembership.features[feature] && selectedMembership.features[feature].locked;
  };

  navigateOrGateFeature = (event, feature) => {
    if (!this.showFeature(feature) || this.lockFeature(feature)) {
      event.preventDefault();
    }

    // Ensure that navigational clicks dismiss the expanded navbar.
    this.setState({ isNavbarExpanded: false });
  };

  toggleNavbar = () => {
    const { isNavbarExpanded } = this.state;
    this.setState({ isNavbarExpanded: !isNavbarExpanded });
  };

  closeNavbar = () => this.setState({ isNavbarExpanded: false });

  setIsWpSideNavigationExpanded = value => this.setState({ isWpSideNavigationExpanded: value });

  generateRedirectUrl = (fromRoute, toRoute, currentLocation) => {
    // Try to match the current location against the from route to extract parameters
    const match = matchPath(currentLocation.pathname, {
      path: fromRoute,
      exact: true,
    });

    let redirectUrl = toRoute;

    // If we have route parameters, generate the new URL with them
    if (match?.params && Object.keys(match.params).length > 0) {
      try {
        redirectUrl = generatePath(toRoute, match.params);
      } catch (error) {
        // If generatePath fails (e.g., toRoute doesn't have the same parameters), fall back to the original route
        console.warn('Failed to generate path with parameters, falling back to original route:', error);
        redirectUrl = toRoute;
      }
    }

    // Add query parameters if they exist
    return `${redirectUrl}${currentLocation.search ? currentLocation.search : ''}`;
  };

  render() {
    const { isNavbarExpanded, redirectTo, firstLoad, isWpSideNavigationExpanded } = this.state;
    const {
      user,
      location,
      app,
      flags: { forceNewAppVersion },
    } = this.props;

    const isModal =
      (location.state && location.state.modal && this.previousLocation !== location) ||
      (this.previousLocation?.state?.modal && this.previousLocation.pathname === location.pathname && !firstLoad) ||
      false;

    const preventPreviousRouteChange = location.state?.preventPreviousRouteChange;
    const lcoationToSwitch = isModal || preventPreviousRouteChange ? this.previousLocation : location;

    return (
      <Suspense fallback={<Spinner />}>
        {redirectTo && <Redirect to={redirectTo} />}
        <SyncPredictiveIndexAuth />
        <UpdateUserTimeZone />
        <WpToastContainer />
        <BannerManager />
        <CommentsAreaWrapper previousLocation={lcoationToSwitch} />
        <GoalDetailPanelWrapper />
        <TrialExpiredModal />
        <TransferToPIBillingModal />
        <AgreeToPiTermsModal />
        <CharmaLegacySunsetModalForcedExperience />
        {app.id === 'zoom' && <ZoomJavascriptSDKHandler />}

        <Switch>
          {this.routesToRedirect.map(route => (
            <Route exact path={route.from} key={`reroute-${route.from}`}>
              <Redirect to={this.generateRedirectUrl(route.from, route.to, location)} />
            </Route>
          ))}
          <RouteLayoutWrapper path={PageUrls.LOGIN_PAGE} layout={LoggedOutWrapper} component={SignInPage} />
          <RouteLayoutWrapper path={PageUrls.SSO_LOGIN_PAGE} layout={LoggedOutWrapper} component={SsoSignInPage} />
          <RouteLayoutWrapper
            path={[PageUrls.RESET_PASSWORD_NEW, PageUrls.RESET_PASSWORD_EDIT]}
            layout={LoggedOutWrapper}
            component={PasswordResetPage}
          />

          <Route path={PageUrls.ZOOM_LANDING_PAGE} component={ZoomLandingPage} />
          <Route exact path="/chrome/uninstall" component={ChromeExtensionUninstall} />
          <Route path="/landing_page" component={LandingPage} />
          <Route path="/goals/new" component={CreateOrUpdateGoalPageWrapper} />
          <Route path={PageUrls.UPDATE_AI_SETTING_PAGE} component={UpdateAiSettingPage} />
          <Route path={PageUrls.UPDATE_AI_REPORT_PAGE} component={UpdateAiReportPage} />
          {/* TODO: need to tie in with A/B flag  */}
          <Route
            exact
            path={[
              '/confirm',
              '/user/login',
              '/user/choose_membership',
              '/user/confirm_desktop_only/:slug?',
              '/user/confirm/:slug?',
              '/mobile_handoff/:slug',
              '/user/confirm',
              '/user/confirmation/new',
              '/user/confirmation',
              // '/user/invitation/accept',
              '/user/invitation/edit',
              '/user/invitation/welcome',
              '/user/invitation',
              // '/invites/:slug',
            ]}
            component={Onboarding}
          />
          <Route exact path={['/user/invitation/accept', '/invites/:slug']} component={AcceptInvitePage} />
          <Route exact path={['/onboarding/welcome', '/onboarding/wizard']} component={OnboardingWrapper}></Route>
          <Route path="/wpds" component={WpdsDocs} />
          <Route
            path={[
              PageUrls.COACHING_360_START,
              PageUrls.COACHING_360_NEW,
              PageUrls.COACHING_SELF_360_NEW,
              PageUrls.PEER_REVIEW_FORM,
              PageUrls.COACHING_360_SHARE,
              PageUrls.COACHING_360_COMPILATION,
            ]}
            component={ThreeSixtyRouteWrapper}
          />
          <Route path={PageUrls.REVIEW_TEMPLATE_PREVIEW} component={ReviewTemplatePreviewPage} />
          <Route
            path={[PageUrls.FEEDBACK_TEMPLATE_BUILD_PAGE, PageUrls.PERFORMANCE_REVIEWS_TEMPLATE_BUILD_PAGE]}
            component={FeedbackTemplateBuilderPage}
          />
          <Route path={PageUrls.PERFORMANCE_REVIEWS_REVIEW_SHARE} component={PerformanceReviewSharePage} exact />
          <Route
            render={() => (
              <div>
                <UnsupportedBrowserAlert />
                <InviteToTeamModal />
                <SimpleBulkInviteTeammatesModal />
                <AddTeammateByEmailModal />
                <UserBaProfileSnapshotRouteDrawer />
                <section className="d-flex wp-app">
                  <SideNavigation
                    isNavbarExpanded={isNavbarExpanded}
                    navigateOrGateFeature={this.navigateOrGateFeature}
                    showFeature={this.showFeature}
                    onCloseNavbar={this.closeNavbar}
                    setIsWpSideNavigationExpanded={this.setIsWpSideNavigationExpanded}
                  />

                  <div
                    className={cn('wp-sidebar-backdrop', {
                      'wp-sidebar-backdrop-show': isNavbarExpanded,
                      'wp-sidebar-backdrop-close': isNavbarExpanded === false,
                    })}
                    onClick={this.toggleNavbar}
                  />

                  <div
                    className={cn('wp-area', {
                      'wp-area--nav-collapsed': isWpSideNavigationExpanded === false,
                      'wp-area--nav-expanded': isWpSideNavigationExpanded === true,
                    })}
                  >
                    <MobileTopNavigation onNavbarToggle={this.toggleNavbar} />
                    <MobileAddButton />
                    <div className="wp-main-content">
                      <Switch location={lcoationToSwitch}>
                        {/* if the page is loaded direclty from a url, isModal is false because previous
                            location does not exist, this routes renders the full page modal properly on top otherwise,
                            if isModal, we trick react-router to render the previous location, 
                            and being no change we maintain the filter state  */}
                        <Route path={PageUrls.SURFACEAI_PAGE} component={HomePage} />
                        <Route path={PageUrls.ADMIN} component={OrgSettingsPage} />

                        <Route path={[PageUrls.COACHING, PageUrls.COACHING_360]} component={CoachingPage} />
                        {/* TODO: with the newer coaching page, should this redirect into /coaching/feedback? */}
                        <Route
                          path={[PageUrls.GOALS_DETAIL_PAGE, PageUrls.GOALS_DETAIL_PAGE_ITEM_COMMENTS]}
                          component={GoalDetailsModal}
                          exact
                        />
                        {/* <Route exact path={PageUrls.HOME_PAGE} component={HomePage} />
                            <Route path={PageUrls.HOME_PAGE_ITEM} component={HomePage} /> */}
                        <Route
                          exact
                          path={[
                            PageUrls.HOME_PAGE,
                            PageUrls.CURRENT_USER_PROFILE_PAGE_HOME,
                            PageUrls.HOME_PAGE_ITEM_COMMENTS,
                          ]}
                          component={HomePage}
                        />
                        <Route path={[PageUrls.PERFORMANCE_REVIEWS]} component={PerformanceReviewsRouteWrapper} />
                        <Route path={PageUrls.UPDATE_AI_PAGE} component={UpdateAiPage} />
                        <Route path={PageUrls.AI_TOOLKIT_PAGE} component={AiToolkitPage} />
                        <Route path={PageUrls.USER_PROFILE_PAGE} component={UserProfilePage} />
                        <Route path={PageUrls.HOME_PAGE_ITEM} component={HomePage} />
                        <Route path={PageUrls.TEAM_PAGE} component={HomePage} />
                        <Route path={[PageUrls.GOALS_PAGE, PageUrls.GOALS_PAGE_MODAL]} component={GoalsPage} />
                        <Route path="/settings" component={SettingsPage} />
                        <Route path="/search" component={SearchPage} />
                        <Route path="/workspaces/accept_member/:slug?" component={WorkspaceAcceptMember} />
                        <Route path={PageUrls.WORKSPACE_PAGE} component={WorkspacePage} />
                        <ManagerRoute path="/assistants">
                          <AssistantsPage />
                        </ManagerRoute>
                        <Route path="/analytics/dashboard" component={ManagerDashboard} />
                        <div className="wp-main-content--indented">
                          <Route exact path="/recommendations" render={() => <Recommendations user={user} />} />
                          <Route exact path="/recommendation_analytics" render={() => <RecommendationAnalytics />} />
                          <Route
                            exact
                            path="/recommendation_analytics/:userId"
                            render={() => <SingleUserAnalytics />}
                          />
                        </div>
                      </Switch>
                      {/* this route intentionally outside of the switch. We trick react-router to render 2 routes,
                          the full page styling of the modal ensures that this route renders on top of previous route
                          which is passed into the switch, thus maintaining the underlying context / view */}
                      {isModal ? (
                        <>
                          <Route path={[PageUrls.GOALS_PAGE_MODAL]} exact>
                            <GoalDetailsModal previousLocationPath={this.previousLocation.pathname} size="lg" />
                          </Route>
                          <Route path={[PageUrls.GOALS_DETAIL_PAGE, PageUrls.GOALS_DETAIL_PAGE_ITEM_COMMENTS]} exact>
                            <GoalDetailsModal previousLocationPath={this.previousLocation.pathname} />
                          </Route>
                        </>
                      ) : null}
                    </div>
                  </div>
                </section>
              </div>
            )}
          />
        </Switch>
        {forceNewAppVersion && <ForceNewAppVersion />}
      </Suspense>
    );
  }
}

const mapStateToProps = state => ({
  app: state.app,
  user: state.user,
  workspaceID: state.workspace && state.workspace.id,
  org: state.organization,
});

const mapDispatchToProps = dispatch => ({
  onChooseMultipleItems: (uuid, params) => dispatch(updateSelectedUuids(uuid, params)),
  dispatch,
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withLDConsumer()(App)));
